import {useEffect, useRef, useSyncExternalStore} from "react";
import {AccountApi} from "../account/AccountApi";
import {shouldPromptEmailVerification} from "../account/emailTrust";
import {applyAccountToUser} from "../account/subscriptionSync";
import {User} from "../account/User";
import {AuthenticatedSessionState} from "../auth/AuthenticatedSessionWaiter";
import {getIdToken, reloadSessionEmailVerified} from "../auth/authProviders";
import {useAuthenticatedSession, useUser} from "../UserProvider";
import {refreshEmailTrust} from "./emailTrustRefresh";
import {subscribeVerificationSend, verificationSendState} from "./verificationSendStore";
import {isVerifiedReturn, withoutVerifiedReturn} from "./verifiedReturn";
import {watchAddressVerification} from "./verificationWatch";

/**
 * Makes a verified address show up in THIS tab, without a sign-out and without a reload.
 *
 * Thin by mandate: every rule belongs to a React-free module — what the api's continue-URL marker is
 * and what the address bar should say afterwards (`./verifiedReturn`), the order a promotion has to
 * happen in (`./emailTrustRefresh`), and when to look for a flip nobody told us about
 * (`./verificationWatch`). What lives here is the wiring only: effects, browser events, timers.
 *
 * <b>Mounted ONCE, at the app root.</b> The callout has two mount points that are alive at the same
 * time (the files panel behind the account sheet), so hanging this off the callout's own hook would
 * run two watches and force two token refreshes for one flip. The app shell is also the only place
 * that is mounted whatever route the marked link lands on.
 *
 * <b>Two routes in, one outcome.</b> Pressing Continue on Firebase's handler page lands here with the
 * marker and promotes immediately. Not pressing it — closing that tab and coming back to this one, the
 * common journey — is caught by the watch, because Firebase's handler is a static page on another
 * origin and can signal this tab through nothing at all.
 */
export function useEmailVerificationPropagation(): void {
	const [user, setUser] = useUser();
	const [sessionState] = useAuthenticatedSession();
	// The REAL session, not the persisted login flag. `user.isLoggedIn` comes out of `localStorage`
	// on the very first render, before the Firebase session has said anything — so gating on it can
	// spend the marker with no live session (`getIdToken` answers null rather than throwing, the
	// account read 401s, and the marker is already gone), can spend it on an ANONYMOUS session, and
	// can race the bootstrap's own `/account` read into overwriting the promotion it just made.
	// `Authenticated` is published by `bootstrapSession` AFTER its `setUser`, and only for a
	// non-anonymous user, which is exactly this effect's precondition and serialises the two writers.
	const signedIn = sessionState === AuthenticatedSessionState.Authenticated;
	const accountId = user.id;
	const sendState = useSyncExternalStore(
		subscribeVerificationSend,
		verificationSendState,
		verificationSendState,
	);

	// Read through a ref so both effects keep dependency lists that do not include `user` — closing
	// over it directly would re-arm them on every account change, starting with the one this very
	// refresh causes. Same reason `useReferralAttribution` holds its refresh this way.
	const promoteRef = useRef<() => Promise<void>>(() => Promise.resolve());
	promoteRef.current = () => refreshEmailTrust({
		// Bound to the account on screen: `getIdToken`'s second argument refuses a token belonging to
		// anyone else (`FirebaseIdentityChangedError`), which is what stops a sign-out-and-in landing
		// mid-flight from writing the NEW account's payload onto a clone of the OLD one.
		forceIdTokenRefresh: () => getIdToken(true, accountId ?? undefined),
		readAccount: () => AccountApi.getAccount(),
		// One projection for every `/account` re-read in the app, so this cannot drift from what the
		// session bootstrap, the plan change and the referral grant each apply.
		applyAccount: (account) => {
			const next = new User(user);
			applyAccountToUser(next, account);
			setUser(next);
		},
	});

	useEffect(() => {
		if (!signedIn || !isVerifiedReturn(window.location.search)) {
			return;
		}
		// The marker is consumed BEFORE the refresh starts, and consuming it means taking it out of the
		// address bar. That single act is what makes this happen exactly once: a re-render, a second
		// mount, a StrictMode double-effect and a later reload of whatever the user bookmarked all
		// re-read a URL that no longer carries a marker. Cleaning up AFTER the await instead would
		// leave a window in which the trigger is still armed — and the refresh it triggers changes the
		// user, which is what re-runs effects.
		window.history.replaceState(window.history.state, "", withoutVerifiedReturn(window.location));
		// LOGGED, unlike the watch's own failures: the marker is spent, so this is the only attempt,
		// and its silent version is a support report reading "I clicked the link and nothing happened"
		// with nothing in the console to say why. The callout stays as it was and the user can still
		// press Resend; the next page load re-reads the account anyway.
		void promoteRef.current().catch((failure: unknown) =>
			console.error("Verified-return refresh failed; the account stays as this tab last read it",
				failure));
	}, [signedIn]);

	// The watch runs only while the callout is telling the user to go and check their inbox — that is
	// the whole window in which somebody might prove the address somewhere this tab cannot see.
	const awaitingProof = sendState.accountId === accountId && sendState.sent
		&& shouldPromptEmailVerification({loggedIn: user.isLoggedIn, trust: user.emailTrust});

	useEffect(() => {
		if (!awaitingProof) {
			return;
		}
		return watchAddressVerification({
			reloadVerified: reloadSessionEmailVerified,
			onProven: () => promoteRef.current(),
			visible: () => document.visibilityState !== "hidden",
			subscribeReturn: (listener) => {
				// Both events, because neither covers the journey alone: switching back to this tab in
				// the same window fires `visibilitychange` and not always `focus`, while returning from
				// another window fires `focus` and not `visibilitychange`.
				window.addEventListener("focus", listener);
				document.addEventListener("visibilitychange", listener);
				return () => {
					window.removeEventListener("focus", listener);
					document.removeEventListener("visibilitychange", listener);
				};
			},
			startPolling: (listener, everyMs) => {
				const handle = window.setInterval(listener, everyMs);
				return () => window.clearInterval(handle);
			},
			now: () => Date.now(),
		});
	}, [awaitingProof, accountId]);
}
